import { useApp } from "../../context/AppContext.js";
import { useAnalyticsTotals } from "../../hooks/useAnalyticsTotals.js";
import { getDateRangePresets } from "../../utils/dateHelpers.js";
import { fmt } from "../../utils/formatters.js";

export function Analytics() {
  const {
    clients, entries,
    analyticsRange: range, setAnalyticsRange: setRange,
    analyticsCustomStart: customStart, setAnalyticsCustomStart: setCustomStart,
    analyticsCustomEnd: customEnd, setAnalyticsCustomEnd: setCustomEnd,
  } = useApp();
  const totals = useAnalyticsTotals(clients, entries, range, customStart, customEnd);

  const presets = getDateRangePresets();
  const activeClients = clients.filter((c) => c.status === "active");

  return (
    <div>
      {/* Range selector */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="range-selector">
          <label className="range-label">Time Period:</label>
          {Object.entries(presets).map(([key, p]) => (
            <button
              key={key}
              className={range === key ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
              onClick={() => setRange(key)}
            >
              {p.label}
            </button>
          ))}
          <button
            className={range === "custom" ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
            onClick={() => setRange("custom")}
          >
            Custom Range
          </button>
        </div>

        {range === "custom" && (
          <div className="custom-range">
            <input
              type="date"
              className="inp"
              style={{ width: 160 }}
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span style={{ color: "var(--gray-400)" }}>to</span>
            <input
              type="date"
              className="inp"
              style={{ width: 160 }}
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        )}
      </div>

      {totals && (
        <>
          {/* Summary stats */}
          <div className="flex-row" style={{ marginBottom: 24 }}>
            <div className="card stat-card">
              <div className="stat-label">Subtotal</div>
              <div className="stat-value" style={{ fontSize: 24 }}>{fmt(totals.grandSubtotal)}</div>
            </div>
            <div className="card stat-card">
              <div className="stat-label">GST (5%)</div>
              <div className="stat-value gst-value" style={{ fontSize: 24 }}>{fmt(totals.grandGST)}</div>
            </div>
            <div className="card stat-card">
              <div className="stat-label">Total</div>
              <div className="stat-value">{fmt(totals.grandTotal)}</div>
              <div className="stat-sub">{totals.entryCount} entries</div>
            </div>
            <div className="card stat-card">
              <div className="stat-label">Total Hours</div>
              <div className="stat-value" style={{ fontSize: 24 }}>{totals.totalHours.toFixed(1)}</div>
            </div>
          </div>

          {/* Revenue breakdown */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 className="section-title">Revenue Breakdown</h3>
            <div className="breakdown-grid">
              {[
                { label: "Hourly Earnings", value: totals.totalHourlyCharges, color: "var(--blue)" },
                { label: "Task-Based Earnings", value: totals.totalTaskCharges, color: "var(--purple)" },
                { label: "Fixed Fee Earnings", value: totals.totalFixedCharges, color: "var(--orange)" },
                { label: "GST (5%)", value: totals.grandGST, color: "var(--green)" },
              ].map(({ label, value, color }) => (
                <div key={label}>
                  <div className="breakdown-label">{label}</div>
                  <div className="breakdown-value" style={{ color }}>{fmt(value)}</div>
                  {totals.grandSubtotal > 0 && label !== "GST (5%)" && (
                    <div className="breakdown-pct">
                      {((value / totals.grandSubtotal) * 100).toFixed(1)}% of subtotal
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Per-client table */}
          <div className="card">
            <h3 className="section-title">Per-Client Analytics</h3>
            <table>
              <thead>
                <tr>
                  {["Client", "Hours", "Hourly", "Task $", "Fixed", "Subtotal", "GST (5%)", "Total"].map(
                    (h, i) => <th key={i} className={i > 0 ? "right" : ""}>{h}</th>,
                  )}
                </tr>
              </thead>
              <tbody>
                {activeClients
                  .filter((c) => (totals.byClient[c.id]?.total ?? 0) > 0)
                  .sort(
                    (a, b) =>
                      (totals.byClient[b.id]?.total ?? 0) -
                      (totals.byClient[a.id]?.total ?? 0),
                  )
                  .map((c) => {
                    const t = totals.byClient[c.id]!;
                    return (
                      <tr key={c.id}>
                        <td>
                          <div className="client-row">
                            <span className="dot" style={{ background: c.color }} />
                            <span style={{ fontWeight: 600 }}>{c.name}</span>
                          </div>
                        </td>
                        <td className="right">{t.hours.toFixed(1)}</td>
                        <td className="right">{fmt(t.hourlyTotal)}</td>
                        <td className="right">{fmt(t.taskCharges)}</td>
                        <td className="right">{fmt(t.fixedTotal)}</td>
                        <td className="right">{fmt(t.subtotal)}</td>
                        <td className="right">{fmt(t.gst)}</td>
                        <td className="right total-cell">{fmt(t.total)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
